import React, { useState, useEffect } from 'react';
import { useUniversity } from '../contexts/UniversityContext';
import { getSubjectsForUniversity, getTutorsForSubject, getSpecialPackagePricing } from '../config/universities';
import './SpecialPackage.css';

const LOCAL_TESTING = false;
const API_URL = LOCAL_TESTING
  ? 'http://localhost:3001'
  : 'https://tutorly-booking-automation.onrender.com';

const SpecialPackage = () => {
  const { university: universitySlug } = useUniversity();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    typeOfPlan: '',
    sizeOfClass: '',
    subject: '',
    tutorPreferences: '',
    classFormat: '',
    location: '',
    timePreference: '' // Only for ESADE undergraduate
  });

  const [subjects, setSubjects] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculatedPrice, setCalculatedPrice] = useState(null);

  const isEsade = universitySlug === 'undergraduate';

  // Load initial data function
  const loadInitialData = async () => {
    if (!universitySlug) return;
    setLoading(true);
    try {
      const [subjectsData, pricingData] = await Promise.all([
        getSubjectsForUniversity(universitySlug),
        getSpecialPackagePricing(universitySlug)
      ]);
      console.log('Loaded subjects:', subjectsData);
      console.log('Loaded pricing:', pricingData);
      setSubjects(subjectsData || []);
      setPricing(pricingData || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load tutors for selected subject
  const loadTutorsForSubject = async () => {
    if (!formData.subject || !universitySlug) {
      setTutors([]);
      return;
    }
    try {
      const tutorsData = await getTutorsForSubject(formData.subject, universitySlug);
      console.log('Loaded tutors for', formData.subject, ':', tutorsData);
      setTutors(tutorsData || []);
    } catch (error) {
      console.error('Error loading tutors:', error);
      setTutors([]);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universitySlug]);

  useEffect(() => {
    loadTutorsForSubject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.subject, universitySlug]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleButtonSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getPriceForPackage = (plan, size) => {
    if (pricing && pricing.length > 0) {
      const planData = pricing.find(p => p.packageType === plan);
      if (planData) {
        const sizeKey = size.toLowerCase();
        return planData[sizeKey] || 'N/A';
      }
    }
    return 'N/A';
  };

  // Calculate price whenever plan or size changes
  useEffect(() => {
    if (formData.typeOfPlan && formData.sizeOfClass) {
      const priceValue = getPriceForPackage(formData.typeOfPlan, formData.sizeOfClass);
      if (priceValue !== 'N/A' && priceValue !== null && priceValue !== undefined) {
        const numericPrice = typeof priceValue === 'number'
          ? priceValue
          : parseFloat(String(priceValue).replace(',', '.').replace(/[^0-9.]/g, ''));
        setCalculatedPrice(Number.isFinite(numericPrice) ? numericPrice : null);
      } else {
        setCalculatedPrice(null);
      }
    } else {
      setCalculatedPrice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.typeOfPlan, formData.sizeOfClass, pricing]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      alert('Please fill in all required fields');
      return;
    }

    if (!formData.typeOfPlan || !formData.sizeOfClass || !formData.subject || !formData.classFormat) {
      alert('Please complete all class details');
      return;
    }

    // Location is required when classFormat is In-Person
    if (formData.classFormat === 'In-Person' && !formData.location) {
      alert('Please select a location for in-person classes');
      return;
    }

    if (isEsade && !formData.timePreference) {
      alert('Please select a preferred time of day');
      return;
    }

    if (!calculatedPrice) {
      alert('Unable to calculate price. Please try again.');
      return;
    }

    try {
      // Prepare booking data
      const bookingData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        packageType: formData.typeOfPlan,
        classSize: formData.sizeOfClass,
        subject: formData.subject,
        tutorPreferences: formData.tutorPreferences || 'No preference',
        classFormat: formData.classFormat,
        location: formData.classFormat === 'In-Person' ? formData.location : 'N/A',
        timePreference: isEsade ? (formData.timePreference || 'No preference') : 'N/A',
        university: universitySlug,
        bookingType: 'special-package'
      };

      // Create Stripe checkout session
      const response = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: calculatedPrice.toFixed(2),
          customerEmail: formData.email,
          bookingData: bookingData,
          frontendUrl: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const result = await response.json();

      // Save booking data to localStorage for retrieval after payment
      localStorage.setItem('pendingBookingData', JSON.stringify(bookingData));

      // Redirect to Stripe Checkout
      window.location.href = result.url;

    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to proceed to payment. Please try again.');
    }
  };

  if (loading) {
    return <div className="special-package-loading">Loading...</div>;
  }

  return (
    <div className="special-package-page">
      {/* Form Section */}
      <div className="special-package-form-section">
        <div className="special-package-form-outer">
          <div className="special-package-form-wrapper">
            <h3 className="special-package-form-title">Please fill out this form</h3>
            <div className="special-package-form-container">
              <form id="special-package-form" className="special-package-form" onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <div className="form-section">
                  <h4 className="form-section-title">Personal Information</h4>

                  <div className="form-group">
                    <label>Name <span className="required">*</span></label>
                    <div className="form-name-row">
                      <input
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Email <span className="required">*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-full-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone number (include prefix, example: +34)</label>
                    <input
                      type="text"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="form-full-input"
                      placeholder="+34 123 456 789"
                    />
                  </div>
                </div>

                {/* Class Details Section */}
                <div className="form-section">
                  <h4 className="form-section-title">Class Details</h4>

                  <div className="form-group">
                    <label>Type of plan <span className="required">*</span></label>
                    <div className="input-options">
                      <div className="input-radio-card" onClick={() => handleButtonSelect('typeOfPlan', '5 hour')}>
                        <input
                          type="radio"
                          name="typeOfPlan"
                          value="5 hour"
                          checked={formData.typeOfPlan === '5 hour'}
                          onChange={handleInputChange}
                          required
                        />
                        <div className="radio-card-content">
                          <span>5 hours</span>
                        </div>
                      </div>
                      <div className="input-radio-card" onClick={() => handleButtonSelect('typeOfPlan', '10 hours')}>
                        <input
                          type="radio"
                          name="typeOfPlan"
                          value="10 hours"
                          checked={formData.typeOfPlan === '10 hours'}
                          onChange={handleInputChange}
                        />
                        <div className="radio-card-content">
                          <span>10 hours</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Size of class <span className="required">*</span></label>
                    <div className="input-options">
                      {['Solo', 'Duo', 'Trio', 'Quadruple'].map(size => (
                        <div key={size} className="input-radio-card" onClick={() => handleButtonSelect('sizeOfClass', size)}>
                          <input
                            type="radio"
                            name="sizeOfClass"
                            value={size}
                            checked={formData.sizeOfClass === size}
                            onChange={handleInputChange}
                            required
                          />
                          <div className="radio-card-content">
                            <span>{size}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Which subject are you interested in? <span className="required">*</span></label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="form-full-input"
                      required
                    >
                      <option value="">Select a subject</option>
                      {subjects.map((subject) => (
                        <option key={subject.value || subject} value={subject.value || subject}>
                          {subject.label || subject}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Do you have any tutor preferences?</label>
                    <select
                      name="tutorPreferences"
                      value={formData.tutorPreferences}
                      onChange={handleInputChange}
                      className="form-full-input"
                    >
                      <option value="">Select a tutor (optional)</option>
                      <option value="No preference">No preference</option>
                      {tutors.map((tutor, index) => (
                        <option key={index} value={tutor.name}>{tutor.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Class format <span className="required">*</span></label>
                    <div className="input-options">
                      <div className="input-radio-card" onClick={() => handleButtonSelect('classFormat', 'Online')}>
                        <input
                          type="radio"
                          name="classFormat"
                          value="Online"
                          checked={formData.classFormat === 'Online'}
                          onChange={handleInputChange}
                          required
                        />
                        <div className="radio-card-content">
                          <span>Online</span>
                        </div>
                      </div>
                      <div className="input-radio-card" onClick={() => handleButtonSelect('classFormat', 'In-Person')}>
                        <input
                          type="radio"
                          name="classFormat"
                          value="In-Person"
                          checked={formData.classFormat === 'In-Person'}
                          onChange={handleInputChange}
                        />
                        <div className="radio-card-content">
                          <span>In-Person</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location field - only show if In-Person is selected */}
                  {formData.classFormat === 'In-Person' && (
                    <div className="form-group">
                      <label>Class location <span className="required">*</span></label>
                      <div className="input-options">
                        <div className="input-radio-card" onClick={() => handleButtonSelect('location', 'Barcelona')}>
                          <input
                            type="radio"
                            name="location"
                            value="Barcelona"
                            checked={formData.location === 'Barcelona'}
                            onChange={handleInputChange}
                            required
                          />
                          <div className="radio-card-content">
                            <span>Barcelona</span>
                          </div>
                        </div>
                        <div className="input-radio-card" onClick={() => handleButtonSelect('location', 'Sant Cugat')}>
                          <input
                            type="radio"
                            name="location"
                            value="Sant Cugat"
                            checked={formData.location === 'Sant Cugat'}
                            onChange={handleInputChange}
                          />
                          <div className="radio-card-content">
                            <span>Sant Cugat</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Time Preference - only for ESADE (undergraduate) */}
                  {isEsade && (
                    <div className="form-group">
                      <label>Preferred time of day <span className="required">*</span></label>
                      <div className="input-options">
                        <div className="input-radio-card" onClick={() => handleButtonSelect('timePreference', 'Morning')}>
                          <input
                            type="radio"
                            name="timePreference"
                            value="Morning"
                            checked={formData.timePreference === 'Morning'}
                            onChange={handleInputChange}
                            required
                          />
                          <div className="radio-card-content">
                            <span>Morning</span>
                          </div>
                        </div>
                        <div className="input-radio-card" onClick={() => handleButtonSelect('timePreference', 'Afternoon')}>
                          <input
                            type="radio"
                            name="timePreference"
                            value="Afternoon"
                            checked={formData.timePreference === 'Afternoon'}
                            onChange={handleInputChange}
                            required
                          />
                          <div className="radio-card-content">
                            <span>Afternoon</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Price Summary */}
            {calculatedPrice && (
              <div className="price-summary">
                <div className="price-summary-content">
                  <div className="price-row">
                    <span className="price-label">Package:</span>
                    <span className="price-value">{formData.typeOfPlan}</span>
                  </div>
                  <div className="price-row">
                    <span className="price-label">Class Size:</span>
                    <span className="price-value">{formData.sizeOfClass}</span>
                  </div>
                  <div className="price-row total-price">
                    <span className="price-label">Total Price:</span>
                    <span className="price-value">€{calculatedPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="special-package-submit-wrapper">
              <button
                className="button-primary"
                type="submit"
                form="special-package-form"
                disabled={!calculatedPrice}
              >
                {calculatedPrice ? 'Proceed to Payment' : 'Complete Form to See Price'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialPackage;
